import type { AiCreditsResponse, AiCreditsUserUsageRow } from '@protoss/shared';
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  IconSize,
  Pill,
  SkeletonBlock,
  SkeletonProvider,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../components/shared/PageErrorState';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { apiFetch } from '../../lib/apiClient';
import { friendlyErrorMessage } from '../../lib/error';
import './ai-credits-report.css';

/** Superset response - userBreakdown is present only on the admin endpoint. */
type ReportData = AiCreditsResponse & { userBreakdown?: AiCreditsUserUsageRow[] };

export interface AiCreditsReportProps {
  /** API path returning AiCreditsResponse (or AiCreditsAdminResponse), e.g. '/api/workspace/ai-credits' */
  endpoint: string;
  /** Render the per-user breakdown table (admin endpoint only) */
  showUserBreakdown?: boolean;
}

/**
 * Label for a "System" row (userUuid === null) - usage with no user attribution,
 * broken down by token_ledger source_type so admins see what kind of background
 * work it was rather than an opaque "unattributed" bucket.
 */
function systemRowLabel(sourceType: string | null | undefined): string {
  switch (sourceType) {
    case 'embedding':
      return 'System · Document indexing';
    case 'background_run':
      return 'System · Background tasks';
    case 'tool_call':
      return 'System · Automated tools';
    default:
      return 'System';
  }
}

/** Display label for a breakdown row: a real user's name, or a System bucket. */
function userRowLabel(row: AiCreditsUserUsageRow): string {
  if (row.userUuid) return row.displayName ?? row.email ?? 'Unknown user';
  return systemRowLabel(row.systemSourceType);
}

/**
 * Format token count with abbreviations (M for million, K for thousand)
 */
function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/**
 * Tier color assignment - maps tier label to CSS class modifier
 */
const TIER_COLORS: Record<string, string> = {
  fast: 'fast',
  thinking: 'thinking',
  embedding: 'embedding',
};

function getTierColorClass(tierLabel: string): string {
  return TIER_COLORS[tierLabel] ?? 'other';
}

/**
 * Shift a YYYY-MM billing period by a number of months using pure integer
 * arithmetic. Date objects are deliberately avoided here: `new Date('YYYY-MM-DD')`
 * parses as UTC while `setMonth`/`getMonth` operate in local time, which skips or
 * repeats months for users west of UTC.
 */
function shiftPeriod(period: string, deltaMonths: number): string {
  const [y = 0, m = 1] = period.split('-').map(Number);
  const total = y * 12 + (m - 1) + deltaMonths;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Period navigation hook - manages selected billing period (YYYY-MM format)
 * Uses server-provided current period to eliminate timezone ambiguity.
 */
function usePeriodNav(serverCurrentPeriod: string) {
  const minPeriod = React.useMemo(
    () => shiftPeriod(serverCurrentPeriod, -12),
    [serverCurrentPeriod]
  );

  const [period, setPeriod] = React.useState<string>(serverCurrentPeriod);

  const canGoBack = period > minPeriod;
  const canGoForward = period < serverCurrentPeriod;

  function navigate(direction: -1 | 1) {
    setPeriod((p) => shiftPeriod(p, direction));
  }

  // Format in UTC so the label matches the period string regardless of the
  // browser's timezone (a local-time format renders 'YYYY-06-01T00:00Z' as May
  // for users west of UTC).
  const label = new Date(period + '-01T00:00:00Z').toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return { period, label, canGoBack, canGoForward, navigate };
}

/**
 * AI Credits report - token/cost usage and budget status across billing periods,
 * with month-to-month navigation and graceful degradation for workspaces without
 * budgets. Used by the user-level Settings section (own usage) and the workspace
 * admin section (workspace-wide totals + per-user breakdown).
 *
 * Outer component fetches the server's current billing period once, then renders the body with a
 * guaranteed-real period so usePeriodNav initializes correctly (no client-clock fallback / race).
 */
export function AiCreditsReport({ endpoint, showUserBreakdown = false }: AiCreditsReportProps) {
  const [serverCurrentPeriod, setServerCurrentPeriod] = React.useState<string | null>(null);
  const [initialData, setInitialData] = React.useState<ReportData | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const initializePeriod = React.useCallback(async () => {
    setError(null);
    try {
      const result = await apiFetch<ReportData>(endpoint);
      setInitialData(result);
      setServerCurrentPeriod(result.billingPeriod);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Failed to load AI credits. Please try again.'));
    }
  }, [endpoint]);

  React.useEffect(() => {
    void initializePeriod();
  }, [initializePeriod]);

  if (error) {
    return (
      <div className="ai-credits-report">
        <PageErrorState message={error} onRetry={() => void initializePeriod()} />
      </div>
    );
  }

  if (serverCurrentPeriod === null || initialData === null) {
    return (
      <SkeletonProvider loading>
        <div className="ai-credits-report" />
      </SkeletonProvider>
    );
  }

  return (
    <AiCreditsReportBody
      endpoint={endpoint}
      showUserBreakdown={showUserBreakdown}
      serverCurrentPeriod={serverCurrentPeriod}
      initialData={initialData}
    />
  );
}

/**
 * Report body - owns month navigation and per-month data fetching. Mounted only after the
 * server's current period is known, so usePeriodNav initializes with a real value and the
 * forward/back arrows compare against the correct ceiling.
 */
function AiCreditsReportBody({
  endpoint,
  showUserBreakdown,
  serverCurrentPeriod,
  initialData,
}: {
  endpoint: string;
  showUserBreakdown: boolean;
  serverCurrentPeriod: string;
  initialData: ReportData;
}) {
  const { workspace } = useWorkspace();
  const nav = usePeriodNav(serverCurrentPeriod);
  const [data, setData] = React.useState<ReportData | null>(initialData);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = React.useState(0);
  const isFirstFetch = React.useRef(true);

  const showCostBreakdown = workspace?.aiShowCostBreakdown ?? false;

  // Refetch whenever the selected month changes (or on retry). Skip the very first render -
  // initialData already covers serverCurrentPeriod. The cancelled guard prevents an out-of-order
  // response (e.g. rapid arrow clicks) from clobbering a newer month's data.
  React.useEffect(() => {
    if (isFirstFetch.current) {
      isFirstFetch.current = false;
      return;
    }
    let cancelled = false;
    setData(null);
    setError(null);
    void (async () => {
      try {
        const url =
          nav.period === serverCurrentPeriod ? endpoint : `${endpoint}?period=${nav.period}`;
        const result = await apiFetch<ReportData>(url);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(friendlyErrorMessage(err, 'Failed to load AI credits. Please try again.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [endpoint, nav.period, serverCurrentPeriod, reloadNonce]);

  const loading = data === null && !error;
  const totalTokens = data ? data.totalInputTokens + data.totalOutputTokens : 0;

  // Stable tier rows - always render the three known tiers (zeros when a month
  // has no usage) plus any extra labels the server returns (e.g. 'other'), so the
  // chart and table keep an identical layout across months and while loading.
  const displayTiers = React.useMemo(() => {
    const breakdown = data?.modelBreakdown ?? [];
    const byLabel = new Map(breakdown.map((t) => [t.tierLabel, t]));
    const known = ['fast', 'thinking', 'embedding'].map(
      (tierLabel) =>
        byLabel.get(tierLabel) ?? { tierLabel, inputTokens: 0, outputTokens: 0, costUsd: null }
    );
    const extras = breakdown.filter((t) => !TIER_COLORS[t.tierLabel]);
    return [...known, ...extras];
  }, [data]);

  // User rows - while loading, render placeholder rows so the table keeps its
  // shape and the skeletons have somewhere to shimmer.
  const userRows: (AiCreditsUserUsageRow | null)[] = React.useMemo(() => {
    if (data?.userBreakdown) return data.userBreakdown;
    return loading ? [null, null, null] : [];
  }, [data, loading]);

  // Budget status calculation
  const budgetPercent = data?.budget
    ? (parseFloat(data.budget.spentAmountUsd) / parseFloat(data.budget.limitAmountUsd)) * 100
    : 0;

  const budgetStatus =
    data?.budget && parseFloat(data.budget.spentAmountUsd) > parseFloat(data.budget.limitAmountUsd)
      ? 'exceeded'
      : data?.budget && budgetPercent >= data.budget.warnThresholdPct
        ? 'warning'
        : 'ok';

  return (
    <SkeletonProvider loading={loading}>
      <div className="ai-credits-report">
        {error && <PageErrorState message={error} onRetry={() => setReloadNonce((n) => n + 1)} />}

        {/* Period Navigator */}
        <div className="ai-credits-period-nav">
          <button
            className="ai-credits-period-btn"
            onClick={() => nav.navigate(-1)}
            disabled={!nav.canGoBack}
            aria-label="Previous month"
          >
            <ArrowLeftIcon width={IconSize.sm} height={IconSize.sm} />
          </button>
          <p className="ai-credits-period-label" aria-live="polite">
            {nav.label}
          </p>
          <button
            className="ai-credits-period-btn"
            onClick={() => nav.navigate(1)}
            disabled={!nav.canGoForward}
            aria-label="Next month"
          >
            <ChevronRightIcon width={IconSize.sm} height={IconSize.sm} />
          </button>
        </div>

        {/* All sections below stay mounted regardless of loading/empty state so the
            layout never shifts - values render as 0 (or skeletons while loading). */}

        {/* Stat Cards - the skeleton renders INSIDE the real value element (inline,
            em-sized) so the line box is identical to the loaded state and the cards
            never change height while loading. */}
        <div className="ai-credits-stats">
          <div className="ai-credits-stat">
            <div className="ai-credits-stat__value">
              <SkeletonBlock as="span" className="ai-credits-num-skeleton">
                {formatTokenCount(totalTokens)}
              </SkeletonBlock>
            </div>
            <div className="ai-credits-stat__label">Total tokens</div>
          </div>

          <div className="ai-credits-stat">
            <div className="ai-credits-stat__value">
              <SkeletonBlock as="span" className="ai-credits-num-skeleton">
                {formatTokenCount(data?.totalInputTokens ?? 0)}
              </SkeletonBlock>
            </div>
            <div className="ai-credits-stat__label">Input tokens</div>
          </div>

          <div className="ai-credits-stat">
            <div className="ai-credits-stat__value">
              <SkeletonBlock as="span" className="ai-credits-num-skeleton">
                {formatTokenCount(data?.totalOutputTokens ?? 0)}
              </SkeletonBlock>
            </div>
            <div className="ai-credits-stat__label">Output tokens</div>
          </div>

          {showCostBreakdown && (
            <div className="ai-credits-stat ai-credits-stat--cost">
              <div className="ai-credits-stat__value">
                <SkeletonBlock as="span" className="ai-credits-num-skeleton">
                  ${data?.totalCostUsd ? parseFloat(data.totalCostUsd).toFixed(4) : '0.0000'}
                </SkeletonBlock>
              </div>
              <div className="ai-credits-stat__label">Est. cost</div>
            </div>
          )}
        </div>

        {/* Tier Distribution Chart */}
        <div className="ai-credits-chart">
          <h3 className="ai-credits-chart__title">Tier distribution</h3>
          <div className="ai-credits-chart__rows">
            {displayTiers.map((tier) => {
              const tierPct =
                totalTokens > 0 ? (tier.inputTokens + tier.outputTokens) / totalTokens : 0;
              const tierTokens = tier.inputTokens + tier.outputTokens;
              const colorClass = getTierColorClass(tier.tierLabel);

              return (
                <div key={tier.tierLabel} className="ai-credits-chart__row">
                  <span className="ai-credits-chart__row-label">{tier.tierLabel}</span>
                  <div className="ai-credits-chart__bar-track">
                    <div
                      className={`ai-credits-chart__bar-fill ai-credits-chart__bar-fill--${colorClass}`}
                      style={{ width: `${Math.max(tierPct * 100, 2)}%` }}
                      aria-hidden="true"
                    />
                  </div>
                  <span className="ai-credits-chart__row-pct">
                    <SkeletonBlock
                      as="span"
                      className="ai-credits-num-skeleton ai-credits-num-skeleton--sm"
                    >
                      {(tierPct * 100).toFixed(1)}%
                    </SkeletonBlock>
                  </span>
                  <span className="ai-credits-chart__row-count">
                    <SkeletonBlock
                      as="span"
                      className="ai-credits-num-skeleton ai-credits-num-skeleton--sm"
                    >
                      {formatTokenCount(tierTokens)}
                    </SkeletonBlock>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Budget Panel */}
        {data?.budget && (
          <div className="ai-credits-budget">
            <div className="ai-credits-budget__header">
              <h3 className="ai-credits-budget__title">Budget</h3>
              <span className="ai-credits-budget__amounts">
                ${parseFloat(data.budget.spentAmountUsd).toFixed(2)} / $
                {parseFloat(data.budget.limitAmountUsd).toFixed(2)}
              </span>
            </div>

            <SkeletonBlock className="ai-credits-budget-track-skeleton">
              <div
                className="ai-credits-budget__track"
                role="progressbar"
                aria-valuenow={Math.min(budgetPercent, 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Budget used"
              >
                <div
                  className={`ai-credits-budget__fill ai-credits-budget__fill--${budgetStatus}`}
                  style={{ width: `${Math.min(budgetPercent, 100)}%` }}
                />
              </div>
            </SkeletonBlock>

            <div className="ai-credits-budget__meta">
              <div>
                <Pill
                  variant={
                    budgetStatus === 'ok'
                      ? 'success'
                      : budgetStatus === 'warning'
                        ? 'warning'
                        : 'danger'
                  }
                >
                  {budgetStatus === 'exceeded'
                    ? 'Over budget'
                    : budgetStatus === 'warning'
                      ? 'Warning'
                      : 'Within budget'}
                </Pill>
              </div>
              {data.budget.hardEnforce && (
                <span className="ai-credits-budget__enforce-note">Hard limit enforced</span>
              )}
            </div>
          </div>
        )}

        {/* Tier Breakdown Table */}
        <div className="ai-credits-tiers">
          <div className="ai-credits-tiers__header">
            <h3 className="ai-credits-tiers__title">Tier breakdown</h3>
          </div>
          <table className="ai-credits-tiers__table">
            <thead>
              <tr>
                <th className="ai-credits-tiers__th" style={{ textAlign: 'left' }}>
                  Tier
                </th>
                <th className="ai-credits-tiers__th">Input</th>
                <th className="ai-credits-tiers__th">Output</th>
                {showCostBreakdown && <th className="ai-credits-tiers__th">Cost</th>}
              </tr>
            </thead>
            <tbody>
              {displayTiers.map((tier) => (
                <tr key={tier.tierLabel}>
                  <td className="ai-credits-tiers__td ai-credits-tiers__td--label">
                    {tier.tierLabel}
                  </td>
                  <td className="ai-credits-tiers__td ai-credits-tiers__td--right">
                    <SkeletonBlock
                      as="span"
                      className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                    >
                      {tier.inputTokens.toLocaleString()}
                    </SkeletonBlock>
                  </td>
                  <td className="ai-credits-tiers__td ai-credits-tiers__td--right">
                    <SkeletonBlock
                      as="span"
                      className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                    >
                      {tier.outputTokens.toLocaleString()}
                    </SkeletonBlock>
                  </td>
                  {showCostBreakdown && (
                    <td className="ai-credits-tiers__td ai-credits-tiers__td--right">
                      <SkeletonBlock
                        as="span"
                        className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                      >
                        {tier.costUsd ? `$${parseFloat(tier.costUsd).toFixed(4)}` : '-'}
                      </SkeletonBlock>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* User Breakdown Table (admin only) */}
        {showUserBreakdown && (
          <div className="ai-credits-tiers">
            <div className="ai-credits-tiers__header">
              <h3 className="ai-credits-tiers__title">User breakdown</h3>
            </div>
            <table className="ai-credits-tiers__table">
              <thead>
                <tr>
                  <th className="ai-credits-tiers__th" style={{ textAlign: 'left' }}>
                    User
                  </th>
                  <th className="ai-credits-tiers__th">Input</th>
                  <th className="ai-credits-tiers__th">Output</th>
                  {showCostBreakdown && <th className="ai-credits-tiers__th">Cost</th>}
                </tr>
              </thead>
              <tbody>
                {userRows.length === 0 && (
                  <tr>
                    <td
                      className="ai-credits-tiers__td ai-credits-users__empty"
                      colSpan={showCostBreakdown ? 4 : 3}
                    >
                      No usage recorded for {nav.label}.
                    </td>
                  </tr>
                )}
                {userRows.map((row, index) => (
                  <tr
                    key={
                      row
                        ? (row.userUuid ?? `system-${row.systemSourceType ?? 'other'}`)
                        : `placeholder-${index}`
                    }
                  >
                    <td className="ai-credits-tiers__td ai-credits-tiers__td--label">
                      <SkeletonBlock
                        as="span"
                        className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                      >
                        {row ? userRowLabel(row) : ''}
                      </SkeletonBlock>
                      {row?.email && <div className="ai-credits-users__email">{row.email}</div>}
                    </td>
                    <td className="ai-credits-tiers__td ai-credits-tiers__td--right">
                      <SkeletonBlock
                        as="span"
                        className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                      >
                        {(row?.inputTokens ?? 0).toLocaleString()}
                      </SkeletonBlock>
                    </td>
                    <td className="ai-credits-tiers__td ai-credits-tiers__td--right">
                      <SkeletonBlock
                        as="span"
                        className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                      >
                        {(row?.outputTokens ?? 0).toLocaleString()}
                      </SkeletonBlock>
                    </td>
                    {showCostBreakdown && (
                      <td className="ai-credits-tiers__td ai-credits-tiers__td--right">
                        <SkeletonBlock
                          as="span"
                          className="ai-credits-num-skeleton ai-credits-num-skeleton--wide"
                        >
                          {row?.costUsd ? `$${parseFloat(row.costUsd).toFixed(4)}` : '-'}
                        </SkeletonBlock>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SkeletonProvider>
  );
}
